import React, { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { twMerge } from "tailwind-merge";
import dropDownDownArrow from "../../assets/icons/dropDownDownArrow.png";
import dropDownUpArrow from "../../assets/icons/dropDownUpArrow.png";
import showToast from "../../utils/toastNotifications";
import ConfirmDeleteModal from "../Modals/ConfirmDeleteModal";
import { formStyles } from "./FormUtils";

const MultiSelectedDropDownWithInput = ({
  label,
  options = [],
  displayKey = "name",
  idKey = "_id",
  defaultSelected = [],
  onSelect,
  onNewCustomItem,
  placeholder = "Type or select",
  isRequired = false,
  validUniqueName = false,
  error = "",
  onFocus = null,
  minLength = 0,
  maxLength = 100,
}) => {
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const [selectedItems, setSelectedItems] = useState(defaultSelected);
  const [inputValue, setInputValue] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [lastAddedTag, setLastAddedTag] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);

  useEffect(() => {
    const isDifferent =
      JSON.stringify(selectedItems) !== JSON.stringify(defaultSelected);
    if (isDifferent) {
      const normalizedOptions = options.map((opt) =>
        (opt?.[displayKey] || "").trim().toLowerCase()
      );
      const updatedItems = defaultSelected.map((item) => {
        const itemName = (item?.[displayKey] || "").trim().toLowerCase();
        const existsInOptions = normalizedOptions.includes(itemName);
        console.log("Item:", item, "Exists in options:", existsInOptions);
        
        // FIXED: Only update isCustom if it's not already explicitly set
        // This preserves the isCustom value from loadTrack or other sources
        return {
          ...item,
          isCustom: item.isCustom !== undefined 
            ? item.isCustom  // Keep existing isCustom value if it's set
            : !existsInOptions  // Otherwise determine by checking options
        };
      });
      setSelectedItems(updatedItems);
    }
  }, [defaultSelected, options]);

  useEffect(() => {
    const el = document.querySelector(
      `[data-option-index="${focusedOptionIndex}"]`
    );
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [focusedOptionIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validTheUniqueName = (input, existingList = []) => {
    const trimmed = input.trim();
    const formatted = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    const isValidLength =
      trimmed.length <= maxLength && trimmed.length >= minLength;
    const isUnique = !existingList.some(
      (opt) => (opt.name || "").toLowerCase() === formatted.toLowerCase()
    );
    return isValidLength && isUnique;
  };

  const filteredOptions = options.filter(
    (opt) =>
      (opt?.[displayKey] || "")
        .toLowerCase()
        .includes(inputValue.toLowerCase().trim()) &&
      !selectedItems.some(
        (a) =>
          (a?.[displayKey] || "").toLowerCase() ===
          (opt?.[displayKey] || "").toLowerCase()
      )
  );

  const handleSelect = (item) => {
    const alreadyAdded = selectedItems.some(
      (a) =>
        (a?.[displayKey] || "").toLowerCase() ===
        (item?.[displayKey] || "").toLowerCase()
    );
    if (!alreadyAdded) {
      const updated = [...selectedItems, item];
      setSelectedItems(updated);
      onSelect && onSelect(updated);
      setLastAddedTag(item[displayKey]);
      setTimeout(() => setLastAddedTag(null), 1500);
    }
    setInputValue("");
    setDropdownOpen(false);
  };

  const confirmRemove = (item) => {
    setToDelete(item);
    setShowConfirmModal(true);
  };

  const executeRemove = () => {
    if (toDelete) {
      const updated = selectedItems.filter(
        (a) =>
          (a?.[displayKey] || "").toLowerCase() !==
          (toDelete?.[displayKey] || "").toLowerCase()
      );
      setSelectedItems(updated);
      onSelect && onSelect(updated);
    }
    setShowConfirmModal(false);
    setToDelete(null);
  };

  const addTypedItems = () => {
    const entries = inputValue
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const allNames = [...options, ...selectedItems].map((item) => ({
      name: item?.[displayKey] || "",
    }));
    let newItems = [];

    for (let entry of entries) {
      const formatted = entry.charAt(0).toUpperCase() + entry.slice(1);
      const alreadySelected = selectedItems.find(
        (a) =>
          (a?.[displayKey] || "").toLowerCase().trim() ===
          formatted.toLowerCase().trim()
      );
      if (alreadySelected) {
        showToast("info", `'${formatted}' is already added.`);
        continue;
      }

      if (validUniqueName && !validTheUniqueName(formatted, allNames)) {
        showToast("warning", `Invalid value: '${formatted}'`);
        continue;
      }

      const existingOption = options.find(
        (opt) =>
          (opt?.[displayKey] || "").toLowerCase() === formatted.toLowerCase()
      );
      const newItem = existingOption || {
        [displayKey]: formatted,
        isCustom: true,
        isManuallyAdded: true, // Mark as manually added by user
      };
      newItems.push(newItem);
      if (!existingOption && onNewCustomItem) onNewCustomItem(newItem);
    }

    if (newItems.length > 0) {
      const updated = [...selectedItems, ...newItems];
      setSelectedItems(updated);
      onSelect && onSelect(updated);
      setLastAddedTag(newItems[newItems.length - 1][displayKey]);
      setTimeout(() => setLastAddedTag(null), 1500);
    }

    setInputValue("");
    setDropdownOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setDropdownOpen(true);
      setFocusedOptionIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setDropdownOpen(true);
      setFocusedOptionIndex((prev) =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1
      );
    }

    if (e.key === "Enter") {
      if (dropdownOpen && filteredOptions[focusedOptionIndex]) {
        e.preventDefault();
        handleSelect(filteredOptions[focusedOptionIndex]);
      } else if (inputValue.trim()) {
        addTypedItems();
      }
    }

    if (e.key === " " && !dropdownOpen) {
      e.preventDefault();
      setDropdownOpen(true);
    }

    if (e.key === "Escape") {
      setDropdownOpen(false);
    }

    if (e.key === "Backspace" && inputValue === "") {
      const last = selectedItems[selectedItems.length - 1];
      if (last) confirmRemove(last);
    }
  };

  return (
    <div ref={wrapperRef} className="w-full relative text-white">
      {label && (
        <div className="flex items-center gap-2">
          <label
            className={twMerge(
              "text-light-gray font-inter font-medium text-sm",
              formStyles.dropDownlabel
            )}
          >
            {label} {isRequired && <span className="text-light-red">*</span>}
          </label>
        </div>
      )}

      <div className="relative">
        <div
          className={twMerge(
            "w-full h-[45px] pr-10 pl-3 py-2 bg-[#1C1C1C] text-white flex flex-wrap items-center gap-2 rounded-lg",
            error
              ? "border border-red-500 "
              : "border border-[#808080] bg-[#45444433]"
          )}
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.focus();
            setDropdownOpen((prev) => !prev);
          }}
          onFocus={onFocus}
        >
          {selectedItems.map((item, i) => (
            <span
              key={i}
              className={twMerge(
                "flex items-center gap-1 px-3 py-1 text-sm rounded transition-all duration-300",
                item?.isManuallyAdded
                  ? "bg-gradient-to-r from-[#52FAF0] to-[#197AD0] text-white"
                  : "bg-[#454444] text-white",
                lastAddedTag &&
                  (item?.[displayKey] || "").toLowerCase() ===
                    lastAddedTag?.toLowerCase()
                  ? "ring-2 ring-[#51F9EF] scale-105"
                  : ""
              )}
            >
              {item?.[displayKey] || ""}
              <IoClose
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  confirmRemove(item);
                }}
              />
            </span>
          ))}

          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setDropdownOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={selectedItems.length === 0 ? placeholder : ""}
            className="bg-transparent flex-grow text-white placeholder:text-[#808080] placeholder:text-[14px] text-[14px] focus:outline-none min-w-[80px]"
          />
        </div>

        <div
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
          onClick={() => setDropdownOpen((prev) => !prev)}
        >
          <img
            src={dropdownOpen ? dropDownDownArrow : dropDownUpArrow}
            alt="arrow"
            className="w-[12px] h-[8px]"
          />
        </div>
      </div>

      {dropdownOpen && (
        <div className="absolute z-10 bg-[#454444] mt-1 w-full rounded shadow max-h-[200px] overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option[idKey] || option[displayKey]}
                data-option-index={index}
                className={twMerge(
                  "px-4 py-2 border-[1px] border-[#81818180] text-[#E2E2E2] cursor-pointer",
                  index === focusedOptionIndex && "bg-gray-600 text-white"
                )}
                onMouseEnter={() => setFocusedOptionIndex(index)}
                onMouseLeave={() => setFocusedOptionIndex(-1)}
                onClick={() => handleSelect(option)}
              >
                {option[displayKey]}
              </div>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-400 italic">
              No results found
            </div>
            )}
        </div>
      )}

      {showConfirmModal && (
        <ConfirmDeleteModal
          visible={showConfirmModal}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={executeRemove}
          title="Confirm Deletion"
          content={
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-[#F87171]">
                '{toDelete?.[displayKey]}'
              </span>{" "}
              ?<br />
            </>
          }
        />
      )}
      {error && (
        <div className="text-[#FF0000] text-[12px] leading-[10px] font-Inter mt-3 ml-1">
          {error}
        </div>
      )}
    </div>
  );
};

export default MultiSelectedDropDownWithInput;
